//v3
import { useEffect, useRef } from "react";
import { useMap } from "react-leaflet";

interface Props {
  setMapCenter: (center: [number, number]) => void;
  setMapZoom: (zoom: number) => void;
  isRemoteUpdate?: boolean; // optional flag to suppress feedback
}


// Consolidated debouncing and synchronization logic for all map events
const MapEventsSync: React.FC<Props> = ({
  setMapCenter,
  setMapZoom,
  isRemoteUpdate = false,
}) => {
  const map = useMap();
  const debounceTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastBroadcastRef = useRef<{ center: [number, number]; zoom: number } | null>(null);
  const isRemoteUpdateRef = useRef(isRemoteUpdate);

  useEffect(() => {
    isRemoteUpdateRef.current = isRemoteUpdate;
  }, [isRemoteUpdate]);

  useEffect(() => {
    const broadcastSync = () => {
      if (isRemoteUpdateRef.current) return;
      const center = map.getCenter();
      const zoom = map.getZoom();
      // Only broadcast if changed
      if (
        !lastBroadcastRef.current ||
        lastBroadcastRef.current.center[0] !== center.lat ||
        lastBroadcastRef.current.center[1] !== center.lng ||
        lastBroadcastRef.current.zoom !== zoom
      ) {
        setMapCenter([center.lat, center.lng]);
        setMapZoom(zoom);
        lastBroadcastRef.current = { center: [center.lat, center.lng], zoom };
      }
    };

    const onMoveOrZoomEnd = () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
      debounceTimeout.current = setTimeout(broadcastSync, 300);
    };

    map.on("moveend", onMoveOrZoomEnd);
    map.on("zoomend", onMoveOrZoomEnd);

    return () => {
      map.off("moveend", onMoveOrZoomEnd);
      map.off("zoomend", onMoveOrZoomEnd);
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
    };
  }, [map, setMapCenter, setMapZoom]);

  return null;
};

export default MapEventsSync;

